import { useState, useCallback } from 'react';
import { UploadFile } from 'antd/es/upload/interface';
import { Upload } from 'antd';

import { setShowToast } from '../../../../../redux/slice/rfpModuleSlice/application';
import { removeFileFromList, deleteFile } from '../../../../../redux/slice/rfpModuleSlice/overview';

interface UseFileManagementProps {
  currentQuestion: number;
}

interface FileManagementState {
  questionFiles: Record<number, UploadFile[]>;
  uploading: boolean;
  fileTypeError: string | null;
  deleteModal: {
    visible: boolean;
    file: UploadFile | null;
    uploadedFile?: any | null;
  };
  isDelete: boolean;
  deleteError: string | null;
  deleteModalType: 'local' | 'uploaded' | null;
}

export const useFileManagement = ({ currentQuestion }: UseFileManagementProps) => {
  const [state, setState] = useState<FileManagementState>(() => ({
    questionFiles: (() => {
      try {
        const saved = localStorage.getItem("questionFiles");
        return saved ? JSON.parse(saved) : {};
      } catch {
        return {};
      }
    })(),
    uploading: false,
    fileTypeError: null,
    deleteModal: { visible: false, file: null, uploadedFile: null },
    isDelete: false,
    deleteError: null,
    deleteModalType: null,
  }));

  const persistFiles = useCallback((filesByQuestion: Record<number, UploadFile[]>) => {
    localStorage.setItem("questionFiles", JSON.stringify(filesByQuestion));
  }, []);

  const beforeUpload = useCallback((file: File) => {
    const allowed = ["pdf", "doc", "docx", "csv", "xls", "xlsx"];
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!ext || !allowed.includes(ext)) {
      setState(prev => ({ ...prev, fileTypeError: "Invalid file type - Only PDF, DOC, DOCX, CSV, XLS, XLSX allowed." }));
      setTimeout(() => setState(prev => ({ ...prev, fileTypeError: null })), 3000);
      return Upload.LIST_IGNORE;
    }
    if (file.size > 25 * 1024 * 1024) {
      setState(prev => ({ ...prev, fileTypeError: "File too large - Max size is 25MB." }));
      setTimeout(() => setState(prev => ({ ...prev, fileTypeError: null })), 3000);
      return Upload.LIST_IGNORE;
    }
    return true;
  }, []);

  const hasQuestionPrefix = useCallback((fileName: string): boolean => {
    return /^Q\d+_/.test(fileName);
  }, []);

  const addQuestionPrefixToFile = useCallback((file: File): File => {
    if (hasQuestionPrefix(file.name)) {
      return file;
    }

    const questionNumber = currentQuestion + 1;
    const fileExtension = file.name.split('.').pop();
    const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
    const newFileName = `Q${questionNumber}_${fileNameWithoutExt}.${fileExtension}`;
    
    return new File([file], newFileName, {
      type: file.type,
      lastModified: file.lastModified,
    });
  }, [currentQuestion, hasQuestionPrefix]);

  const handleFilesChange = useCallback((info: { fileList: UploadFile[] }) => {
    const processedFileList: UploadFile[] = info.fileList.map((file) => {
      if (file.originFileObj) {
        const renamedFile = addQuestionPrefixToFile(file.originFileObj);
        return {
          ...file,
          name: renamedFile.name,
          originFileObj: renamedFile as any,
        } as UploadFile;
      }
      return file;
    });

    setState(prev => {
      const updated = { ...prev.questionFiles, [currentQuestion]: processedFileList };
      persistFiles(updated);
      return { ...prev, questionFiles: updated };
    });
  }, [currentQuestion, addQuestionPrefixToFile, persistFiles]);

  const handleRemove = useCallback((file: UploadFile) => {
    setState(prev => ({
      ...prev,
      deleteModal: { visible: true, file, uploadedFile: null },
      deleteError: null,
      deleteModalType: 'local',
    }));
  }, []);

  const handleRemoveUploadedFile = useCallback((uploadedFile: any) => {
    setState(prev => ({
      ...prev,
      deleteModal: { visible: true, file: null, uploadedFile },
      deleteError: null,
      deleteModalType: 'uploaded',
    }));
  }, []);

  const handleRemoveFile = useCallback((file: any) => {
    if (file.isLocal) {
      handleRemove(file.uploadFile);
    } else {
      handleRemoveUploadedFile(file);
    }
  }, [handleRemove, handleRemoveUploadedFile]);

  const handleConfirmDelete = useCallback(async (dispatch: any) => {
    if (!state.deleteModal.file && !state.deleteModal.uploadedFile) return;
    
    setState(prev => ({ ...prev, isDelete: true, deleteError: null }));
    
    try {
      if (state.deleteModalType === 'uploaded' && state.deleteModal.uploadedFile) {
        dispatch(removeFileFromList(state.deleteModal.uploadedFile.file_id));
        await dispatch(deleteFile(state.deleteModal.uploadedFile.file_id, "RFP"));
      } else if (state.deleteModalType === 'local' && state.deleteModal.file) {
        setState(prev => {
          const updated = { ...prev.questionFiles };
          if (updated[currentQuestion]) {
            updated[currentQuestion] = updated[currentQuestion].filter(
              (f) => f.uid !== state.deleteModal.file?.uid
            );
          }
          persistFiles(updated);
          return { ...prev, questionFiles: updated };
        });
      }
      
      setState(prev => ({
        ...prev,
        deleteModal: { visible: false, file: null, uploadedFile: null },
        deleteModalType: null,
      }));
    } catch (error) {
      setState(prev => ({ ...prev, deleteError: "Failed to delete file. Please try again." }));
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Failed to delete file!",
        })
      );
    } finally {
      setState(prev => ({ ...prev, isDelete: false }));
    }
  }, [state.deleteModal, state.deleteModalType, currentQuestion, persistFiles]);

  const handleCancelDelete = useCallback(() => {
    setState(prev => ({
      ...prev,
      deleteModal: { visible: false, file: null, uploadedFile: null },
      deleteError: null,
      deleteModalType: null,
    }));
  }, []);

  const setUploading = useCallback((uploading: boolean) => {
    setState(prev => ({ ...prev, uploading }));
  }, []);

  return {
    ...state,
    beforeUpload,
    handleFilesChange,
    handleRemoveFile,
    handleConfirmDelete,
    handleCancelDelete,
    setUploading,
  };
};
